/**
 *  External Modules
 */

import Vue from 'vue'
import { createAuth0Client } from '@auth0/auth0-spa-js'
import { processAndStoreUserData } from '@/helpers/userDataProcessor'
import { SessionPersistence } from '@/helpers/sessionPersistence'

/**
 *  Vue.js Instance Definition
 */

let instance

export const getInstance = () => instance

// Helper to create reactive auth state
function createPluginInstance(auth0Config, redirectUri, onRedirectCallback) {
  return new Vue({
    data() {
      return {
        auth0Client: null,
        isLoading: true,
        isAuthenticated: false,
        user: null,
        error: null,
        config: auth0Config
      }
    },
    async created() {
      this.auth0Client = await createAuth0Client({
        ...this.config,
        domain: this.config.domain,
        clientId: this.config.clientId,
        cacheLocation: 'memory', // Use memory instead of localStorage for security
        useRefreshTokens: true,
        useRefreshTokensFallback: true, // Fallback for better compatibility
        authorizationParams: {
          audience: this.config.audience,
          redirect_uri: redirectUri
        }
      })

      let callbackHandled = false
      try {
        const search = window.location.search
        if (
          (search.includes('code=') || search.includes('error=')) &&
          search.includes('state=')
        ) {
          console.log('🔄 Processing Auth0 callback...')
          const { appState } = await this.auth0Client.handleRedirectCallback()

          // Process user data immediately after successful callback
          await this.refreshState()

          // Wait a bit more to ensure all localStorage operations complete
          await new Promise((resolve) => setTimeout(resolve, 200))

          // Clear any logout flag that might interfere
          try {
            localStorage.removeItem('user_logged_out')
            console.log(
              '🧹 Cleared logout flag after successful authentication'
            )
          } catch {}

          callbackHandled = true
          this.isLoading = false
          await Vue.nextTick()
          this.$emit('authStateChanged', {
            isAuthenticated: this.isAuthenticated,
            user: this.user,
            isLoading: this.isLoading
          })

          // Navigate to intended destination
          onRedirectCallback(appState)
        }
      } catch (error) {
        this.error = error
        console.error('❌ Error during Auth0 callback:', error)
      } finally {
        // Only refresh state if callback wasn't handled
        if (!callbackHandled) {
          await this.refreshState()
          this.isLoading = false
          await Vue.nextTick()
          this.$emit('authStateChanged', {
            isAuthenticated: this.isAuthenticated,
            user: this.user,
            isLoading: this.isLoading
          })
        }
      }
    },
    methods: {
      async loginWithRedirect(options = {}) {
        // Respect explicit logout flag (prevents auto-login loops)
        try {
          if (localStorage.getItem('user_logged_out') === 'true') {
            const base = process.env.VUE_APP_SELFSUFFIX || ''
            const landing = `${base}/landing`.replace(/\/+/, '/')
            window.location.assign(landing)
            return
          }
        } catch {}

        // Guard: if a DOM event is accidentally passed from a template (@click handler),
        // ignore it and use empty options to avoid leaking event into Auth0 params
        try {
          const tag = Object.prototype.toString.call(options)
          if (
            tag.includes('Event') ||
            typeof options?.preventDefault === 'function'
          ) {
            options = {}
          }
        } catch {}

        return this.auth0Client.loginWithRedirect({
          ...options,
          authorizationParams: {
            prompt: 'login',
            ...(options.authorizationParams || {})
          }
        })
      },
      logout(options = {}) {
        // Mark user intent so we don't instantly re-login
        try {
          localStorage.setItem('user_logged_out', 'true')
        } catch {}

        // Clear session persistence data
        SessionPersistence.clearStoredSession()

        return this.auth0Client.logout(options)
      },
      getAccessTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o)
      },
      // Alias kept for existing call sites
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o)
      },
      async refreshState() {
        try {
          this.isAuthenticated = await this.auth0Client.isAuthenticated()
          this.user = this.isAuthenticated
            ? await this.auth0Client.getUser()
            : null

          // Process and store user data when authenticated
          if (this.isAuthenticated && this.user) {
            const userData = await processAndStoreUserData(this)
            //if (userData) {
            //  console.log('✅ User data processing completed successfully')
            //} else {
            //  console.warn('⚠️ User data processing returned null')
            //}

            // Store session for recovery in private browsers
            SessionPersistence.storeSession(this)
          } else {
            // If not authenticated, try to recover from stored session info
            this.tryRecoverFromStorage()
          }

          this.$emit('authStateChanged', {
            isAuthenticated: this.isAuthenticated,
            user: this.user,
            isLoading: this.isLoading
          })
        } catch (e) {
          this.error = e
          console.error('❌ Error in refreshState:', e)
          // Try to recover from storage on error
          this.tryRecoverFromStorage()
        }
      },

      tryRecoverFromStorage() {
        try {
          // First try the enhanced session persistence
          const storedSession = SessionPersistence.getStoredSession()
          if (
            storedSession &&
            storedSession.isAuthenticated &&
            storedSession.user
          ) {
            this.user = storedSession.user
            this.isAuthenticated = true

            // Try to get a fresh token to verify the session
            this.auth0Client
              .getTokenSilently({
                ignoreCache: false,
                timeoutInSeconds: 5
              })
              .then((token) => {
                if (token) {
                  console.log('✅ Session recovery successful - token obtained')
                  SessionPersistence.storeSession(this) // Refresh the stored session
                }
              })
              .catch((error) => {
                console.log(
                  '⚠️ Session recovery partial - stored data used but token failed:',
                  error.message
                )
              })

            console.log('✅ Session recovered from SessionPersistence')
            this.$emit('authStateChanged', {
              isAuthenticated: this.isAuthenticated,
              user: this.user,
              isLoading: this.isLoading
            })
            return
          }

          // Fallback to legacy localStorage recovery
          const storedUserInfo = localStorage.getItem('user_info')
          const useSSO = localStorage.getItem('USE_SSO')

          if (storedUserInfo && useSSO === 'true') {
            const userInfo = JSON.parse(storedUserInfo)
            console.log(
              '🔄 Attempting to recover session from legacy localStorage...'
            )

            // Set basic user info for UI purposes
            this.user = {
              email: userInfo.email,
              name: userInfo.name,
              given_name: userInfo.given_name,
              family_name: userInfo.family_name,
              nickname: userInfo.nickname,
              picture: userInfo.picture,
              email_verified: userInfo.email_verified,
              sub: userInfo.sub
            }

            // Mark as authenticated for route guards
            this.isAuthenticated = true

            // Try to get a fresh token silently to verify the session
            this.auth0Client
              .getTokenSilently({
                ignoreCache: false,
                timeoutInSeconds: 5
              })
              .then((token) => {
                if (token) {
                  console.log(
                    '✅ Legacy session recovery successful - token obtained'
                  )
                  SessionPersistence.storeSession(this) // Store in new format
                }
              })
              .catch((error) => {
                console.log(
                  '⚠️ Legacy session recovery partial - stored data used but token failed:',
                  error.message
                )
              })

            console.log('✅ Session recovered from legacy localStorage')
            this.$emit('authStateChanged', {
              isAuthenticated: this.isAuthenticated,
              user: this.user,
              isLoading: this.isLoading
            })
          }
        } catch (error) {
          console.warn('⚠️ Could not recover session from storage:', error)
        }
      }
    }
  })
}

/**
 *  Vue.js Instance Initialization
 */

export const useAuth0 = ({
  onRedirectCallback = () =>
    window.history.replaceState({}, document.title, window.location.pathname),
  redirectUri = window.location.origin,
  ...pluginOptions
}) => {
  if (instance) return instance
  const auth0Config = { redirectUri, ...pluginOptions }
  instance = createPluginInstance(auth0Config, redirectUri, onRedirectCallback)
  return instance
}

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth0 = useAuth0(options)
  }
}
